import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { type ReactNode, useEffect, useState } from "react";
import ProductInformationTab from "~/features/productDetails/components/PdpTabs/ProductInformationTab";
import { CalculationTab } from "~/features/productDetails/components/PdpTabs/CalculationTab";
import CadTab from "~/features/productDetails/components/PdpTabs/CadTab/CadTab";
import MountingManagerTab from "~/features/productDetails/components/PdpTabs/MountingManagerTab";
import { GreaseAppTab } from "~/features/productDetails/components/PdpTabs/GreaseAppTab";
import { isNonNull } from "~/common/utils/reducers";
import { MoreInformationTab } from "~/features/productDetails/components/PdpTabs/MoreInformationTab";
import { SCROLL_TO_TABS } from "~/features/productDetails/components/DesktopProductDetailsPage";
import { trackPdpTabActivation, trackPdpTabChange } from "~/app/analytics/ga/ga";
import { ConfiguratorTab } from "~/features/productDetails/components/PdpTabs/ConfiguratorTab";
import { MOUNTING_MANAGER_TAB } from "~/utils/constants";
import { useBootstrapConfiguration } from "~/app/config/useAppConfig";

export enum TabId {
  PRODUCT_INFORMATION = "Product Information",
  MORE_INFORMATION = "More Information",
  CALCULATION = "Calculation",
  CAD_DATA = "CAD Data",
  MOUNTING_MANAGER = "Mounting Manager",
  GREASE_APP = "Lubrication",
  CONFIGURATOR = "Configurator",
  SIZING = "Sizing",
}

export enum WebcomponentToggle {
  MOUNTING_MANAGER = "mountingManager",
  GREASE_APP = "greaseApp",
  EASY_CALC = "easyCalc",
}

export const TAB_ORDER: TabId[] = [
  TabId.PRODUCT_INFORMATION,
  TabId.MORE_INFORMATION,
  TabId.CALCULATION,
  TabId.CAD_DATA,
  TabId.MOUNTING_MANAGER,
  TabId.GREASE_APP,
  TabId.CONFIGURATOR,
  TabId.SIZING,
];

type Details = {
  isVisible: (product: ProductDescriptor<PdpPageProductData>) => boolean;
  renderTab: (product: ProductDescriptor<PdpPageProductData>) => ReactNode;
  title: string;
  testId: string;
  action?: (product: ProductDescriptor<PdpPageProductData>) => void;
};

export const usePdpTabs = (product: ProductDescriptor<PdpPageProductData>, activePDPTab: TabId) => {
  const { preferredTab: initialTab, fromUrl } = calculateInitialTab(product, activePDPTab);
  const [activeTabIndex, setActiveTab] = useState<string>(initialTab);

  const onTabActivationHandler = (tabId: string) => {
    setActiveTab(tabId);
    trackPdpTabChange(tabId);
    trackPdpTabActivation(activeTabIndex, false);
    trackPdpTabActivation(tabId, true);
    window.location.hash = tabId;
    SCROLL_TO_TABS();
    const details = tabDetails[tabId as TabId];
    if (details?.action) {
      details.action(product);
    }
  };

  useEffect(() => {
    if (fromUrl) {
      window.setTimeout(SCROLL_TO_TABS, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = TAB_ORDER.map((tabId) => {
    const details = tabDetails[tabId];
    if (!details.isVisible(product)) {
      return null;
    }
    return {
      component: details.renderTab(product),
      title: details.title,
      testId: details.testId,
      value: tabId,
    };
  }).filter(isNonNull);

  return {
    tabs,
    activeTabIndex,
    onTabActivationHandler,
  };
};

const tabDetails: Record<TabId, Details> = {
  [TabId.PRODUCT_INFORMATION]: {
    isVisible: (product) => {
      return (
        product.base.specificationBand.length > 0 ||
        product.base.techImages.length > 0 ||
        product.base.technicalDetails.length > 0 ||
        product.base.commentsFootnotes.length > 0
      );
    },
    renderTab: (product) => <ProductInformationTab product={product.base} />,
    title: "productDetailsPage.tabs.information",
    testId: "product-information-tab",
  },
  [TabId.CALCULATION]: {
    isVisible: (product) => {
      if (product.base.externalProduct) {
        return !!product.base.additionalCalculationUrl;
      }
      return !!product.base.bearingXDesignation;
    },
    renderTab: (product) => <CalculationTab product={product.base} />,
    title: "productDetailsPage.tabs.calculation",
    testId: "calculation-tab",
  },
  [TabId.CAD_DATA]: {
    isVisible: (product) => {
      return true;
    },
    renderTab: (product) => <CadTab product={product.base} />,
    title: "productDetailsPage.tabs.caddata",
    testId: "cad-tab",
  },
  [TabId.MOUNTING_MANAGER]: {
    isVisible: (product) => {
      return !!product.base.mountingManagerURL;
    },
    renderTab: (product) => (
      <MountingManagerTab mountingManagerURL={product.base.mountingManagerURL} designation={product.base.designation} />
    ),
    title: MOUNTING_MANAGER_TAB,
    testId: "mounting-manager-tab",
  },
  [TabId.MORE_INFORMATION]: {
    isVisible: (product) => {
      return (product.base.technicalInfo?.columns?.length ?? 0) > 0;
    },
    renderTab: (product) => <MoreInformationTab data={product.base.technicalInfo} />,
    title: "page.pdp.tab.moreInfo",
    testId: "moreInfo-tab",
  },
  [TabId.GREASE_APP]: {
    isVisible: (product) => {
      return !!product.base.greaseAppUrl;
    },
    renderTab: (product) => (
      <GreaseAppTab greaseAppUrl={product.base.greaseAppUrl} designation={product.base.designation} />
    ),
    title: "productDetailsPage.tabs.grease.title",
    testId: "grease-tab",
  },
  [TabId.CONFIGURATOR]: {
    isVisible: (product) => {
      if (product.base.externalProduct) {
        return !!product.base.lmbtConfigurator;
      }
      return false;
    },
    renderTab: (product) => <ConfiguratorTab product={product.base} />,
    title: "productDetailsPage.tabs.configurator.title",
    testId: "configurator-tab",
  },
  [TabId.SIZING]: {
    isVisible: (product) => {
      return !!product.base.sizingUrl;
    },
    renderTab: (product) => null,
    action: (product) => {
      window.open(product.base.sizingUrl);
    },
    title: "productDetailsPage.tabs.sizing.title",
    testId: "sizing-tab",
  },
};

export function calculateInitialTab(
  product: ProductDescriptor<PdpPageProductData>,
  activePDPTab: TabId,
): {
  preferredTab: TabId | "";
  fromUrl: boolean;
} {
  const url = new URL(window.location.href);
  const hash = decodeURI(url.hash.substring(1));

  let fromUrl = false;
  let preferredTab: TabId | "";
  if (hash) {
    // New mode
    preferredTab = hash as TabId;
    fromUrl = true;
  } else {
    preferredTab = "";
  }

  if (activePDPTab && tabDetails[activePDPTab]?.isVisible(product)) {
    return {
      preferredTab: activePDPTab,
      fromUrl,
    };
  }

  if (preferredTab && tabDetails[preferredTab]?.isVisible(product)) {
    return {
      preferredTab,
      fromUrl,
    };
  }

  if (tabDetails[TabId.PRODUCT_INFORMATION]?.isVisible(product)) {
    return {
      preferredTab: TabId.PRODUCT_INFORMATION,
      fromUrl,
    };
  }

  return {
    preferredTab: "",
    fromUrl,
  };
}

export function useIsWebComponentEnabled(webcomponentToggle: string) {
  return useBootstrapConfiguration()?.webComponentEnabledTabs?.includes(webcomponentToggle) ?? false;
}
